/**
 * Routes /babies/{babyId}/events traffic through the local Dexie repo + the
 * mutation queue so the UI is offline-first. Other API paths pass through
 * untouched. This is the bridge between the existing api/client.js call
 * shape and the offline-first data layer.
 *
 * The sync engine (services/sync/*) drains the queue and pulls deltas; this
 * file only knows about local reads/writes.
 *
 * Local ids: a row created here gets `local-<uuid>`. The server assigns the
 * SAME uuid with the prefix stripped (clientEventId → eventId), so once the
 * push rewrites the row to `<uuid>` a caller still holding `local-<uuid>` can
 * be resolved deterministically — see resolveEventId(). A `local-` id never
 * falls through to the network: the server has never heard of it.
 */

import { getCachedAuthUser, NetworkError } from '../network';
import * as eventsRepo from './events';
import * as queue from './queue';

const EVENTS_RE = /^\/babies\/([^/]+)\/events(?:\/([^/]+))?$/;
const LOCAL_PREFIX = 'local-';

export function parseEventsPath(path) {
  if (!path) return null;
  // Strip leading slashes if missing for any reason
  const normalized = path.startsWith('/') ? path : `/${path}`;
  const m = normalized.match(EVENTS_RE);
  if (!m) return null;
  // /babies/{id}/events/twin is a fan-out endpoint — keep on network path
  if (m[2] === 'twin') return null;
  return { babyId: m[1], eventId: m[2] || null };
}

let syncRequest = () => {};
export function registerSyncRequest(fn) {
  if (typeof fn === 'function') syncRequest = fn;
}
function requestSync() {
  try { syncRequest(); } catch { /* sync engine missing is fine in Phase B */ }
}

function currentUserId() {
  return getCachedAuthUser()?.sub || null;
}

export function isLocalId(id) {
  return typeof id === 'string' && id.startsWith(LOCAL_PREFIX);
}

/**
 * Find the Dexie row an update/delete should target.
 *
 *   - row present under `eventId`            → that id
 *   - `local-U` missing but `U` present      → 'U' (the push already rewrote it)
 *   - `local-U` missing and `U` missing      → throw a transient NetworkError:
 *       the create is mid-flight (push.js dropped the orphan and the next pull
 *       re-fetches it) or the row was just deleted. Callers treat it like an
 *       offline blip and retry; it must NEVER reach the network as `local-U`.
 *   - real id missing                        → null (caller falls through to
 *       the network — e.g. a twin-created row we have not pulled yet)
 */
export async function resolveEventId(eventId) {
  if (!eventId) return null;
  const existing = await eventsRepo.get(eventId);
  if (existing) return eventId;
  if (!isLocalId(eventId)) return null;

  const stripped = eventId.slice(LOCAL_PREFIX.length);
  const rewritten = await eventsRepo.get(stripped);
  if (rewritten) return stripped;

  throw new NetworkError('That entry is still syncing — please try again in a moment');
}

export async function routeGet(path, queryParams) {
  const parsed = parseEventsPath(path);
  if (!parsed) return null;
  // A specific eventId GET — uncommon; keep on the network path
  if (parsed.eventId) return null;

  const rows = await eventsRepo.listForBaby(parsed.babyId, queryParams || {});
  // Mirror server array shape; call sites already normalise with
  // `res.items || res.events || res || []` so returning the bare array is safe.
  return rows;
}

export async function routePost(path, body) {
  const parsed = parseEventsPath(path);
  if (!parsed) return null;
  if (parsed.eventId) return null;

  const row = await eventsRepo.createLocal({
    userId: currentUserId(),
    babyId: parsed.babyId,
    payload: body || {},
  });
  await queue.enqueue({
    entity: 'event',
    op: 'create',
    recordId: row.eventId,
    babyId: parsed.babyId,
    // clientEventId lets the server dedupe on retry: if a previous attempt
    // already wrote the row but the response was lost (e.g. 12s timeout on
    // slow Capacitor cellular), the next drain re-POSTs with the same key
    // and the server returns the original row instead of duplicating.
    payload: { ...body, babyId: parsed.babyId, clientEventId: row.eventId },
  });
  requestSync();
  return row;
}

export async function routePut(path, body) {
  const parsed = parseEventsPath(path);
  if (!parsed || !parsed.eventId) return null;

  const eventId = await resolveEventId(parsed.eventId);
  if (!eventId) return null;

  const updated = await eventsRepo.updateLocal(eventId, body || {});
  if (!updated) return null;

  // If this row's create is still queued and has never been dispatched, fold
  // the update into it rather than queueing a second op. Otherwise the pair
  // replays as create-then-update: the create goes out verbatim (an offline
  // sleep start still says `endTime: null`), so the server opens the sleep and
  // pushes 'Baby is asleep' to the co-parent for a sleep that ended hours ago,
  // then immediately pushes 'Sleep ended'. One merged POST is also one round
  // trip, and skips the waiting-for-create 404 retries entirely.
  const merged = await queue.mergeIntoPendingCreate(eventId, body || {});
  if (!merged) {
    await queue.enqueue({
      entity: 'event',
      op: 'update',
      recordId: eventId,
      babyId: parsed.babyId,
      payload: body || {},
    });
  }
  requestSync();
  return updated;
}

export async function routeDelete(path) {
  const parsed = parseEventsPath(path);
  if (!parsed || !parsed.eventId) return null;

  const eventId = await resolveEventId(parsed.eventId);
  if (!eventId) return null;

  if (isLocalId(eventId)) {
    // The row has not been pushed yet. Two cases:
    //   - its create is still 'pending'/'failed' → nothing has reached the
    //     server: hard-delete locally and drop every queue entry for it;
    //   - its create is 'in_flight' (e.g. "Undo" tapped a second after Start),
    //     or was reclaimed from a session that died mid-request (`maybeSent`,
    //     see queue.reclaimStaleInFlight) → the server may already own the
    //     row. Tombstone locally, drop the not-yet-sent follow-ups, KEEP the
    //     create so it still resolves, and queue a DELETE; push.js addresses
    //     it to the stripped id (the server's deterministic id) whether or not
    //     the create's response ever comes back.
    const db = (await import('./index')).getDB();
    let createInFlight = false;
    await db.transaction('rw', db.events, db.mutationQueue, async () => {
      const pending = await db.mutationQueue
        .where('recordId')
        .equals(eventId)
        .toArray();
      const keep = new Set(
        pending
          .filter((p) => p.op === 'create' && (p.status === 'in_flight' || p.maybeSent))
          .map((p) => p.id)
      );
      createInFlight = keep.size > 0;
      if (createInFlight) {
        for (const p of pending) {
          if (!keep.has(p.id)) await db.mutationQueue.delete(p.id);
        }
        const existing = await db.events.get(eventId);
        if (existing) {
          const now = new Date().toISOString();
          await db.events.put({
            ...existing,
            deletedAt: now,
            syncStatus: 'local',
            localUpdatedAt: now,
          });
        }
      } else {
        await db.events.delete(eventId);
        for (const p of pending) await db.mutationQueue.delete(p.id);
      }
    });
    if (createInFlight) {
      await queue.enqueue({
        entity: 'event',
        op: 'delete',
        recordId: eventId,
        babyId: parsed.babyId,
      });
      requestSync();
    }
    return { success: true };
  }

  await eventsRepo.softDelete(eventId);
  await queue.enqueue({
    entity: 'event',
    op: 'delete',
    recordId: eventId,
    babyId: parsed.babyId,
  });
  requestSync();
  return { success: true };
}
